<template>
  <div class="page-container-v2">
    <page-header title="브라이트몰 주문 조회" />

    <!-- 검색 조건 섹션 -->
    <div class="filter-section" :class="{ 'filter-expanded': filterExpanded }">
      <div class="filter-section-header" @click="isMobile && toggleFilter()">
        <span class="filter-title">검색 조건</span>
        <span class="filter-hint">최대 180일까지 조회 가능</span>
        <span v-if="isMobile" class="filter-toggle-icon">{{ filterExpanded ? '▲' : '▼' }}</span>
      </div>
      <div class="filter-body">
        <div class="filter-row">
          <label class="filter-label">조회 기간</label>
          <div class="quick-dates">
            <a-button
              v-for="preset in datePresets"
              :key="preset.label"
              :type="isActivePreset(preset) ? 'primary' : 'default'"
              size="small"
              @click="applyPreset(preset)"
            >{{ preset.label }}</a-button>
          </div>
          <a-range-picker
            v-model:value="dateRange"
            :allow-clear="false"
            format="YYYY-MM-DD"
            :placeholder="['시작일', '종료일']"
            :disabled-date="disabledDate"
            class="date-picker"
            @change="onDateChange"
          />
        </div>
        <div class="filter-row">
          <label class="filter-label">검색</label>
          <a-input v-model:value="filters.ord_no" placeholder="주문번호" class="filter-input" allow-clear @input="onFilterInput" @pressEnter="fetchOrders" />
          <a-input v-model:value="filters.company_name" placeholder="병원명" class="filter-input" allow-clear @input="onFilterInput" @pressEnter="fetchOrders" />
          <a-input v-model:value="filters.prd_name" placeholder="상품명" class="filter-input" allow-clear @input="onFilterInput" @pressEnter="fetchOrders" />
          <a-select v-model:value="filters.paymethod" placeholder="결제수단 전체" class="filter-select" allow-clear @change="onFilterImmediate">
            <a-select-option value="">전체</a-select-option>
            <a-select-option v-for="pm in paymethods" :key="pm.value" :value="pm.value">{{ pm.label }}</a-select-option>
          </a-select>
          <a-select v-model:value="filters.partner_code" placeholder="입점사 전체" class="filter-select" allow-clear @change="onFilterImmediate">
            <a-select-option value="">전체</a-select-option>
            <a-select-option v-for="p in partnerCodes" :key="p.value" :value="p.value">{{ p.label }}</a-select-option>
          </a-select>
          <a-button type="primary" @click="fetchOrders" :loading="isLoading" :disabled="!!dateRangeWarning">
            <search-outlined /> 검색
          </a-button>
          <a-button @click="resetFilters">초기화</a-button>
        </div>
        <div v-if="dateRangeWarning" class="date-warning">
          <exclamation-circle-outlined /> {{ dateRangeWarning }}
        </div>
      </div>
    </div>

    <!-- 검색 결과 섹션 -->
    <div class="result-section">
      <div v-if="!isLoading && noCustSeqs" class="empty-state">
        <a-empty>
          <template #description>
            <span class="empty-title">담당 거래처가 없습니다</span>
            <p class="empty-desc">배정된 거래처가 없어 주문을 조회할 수 없습니다.<br />관리자에게 문의하세요.</p>
          </template>
        </a-empty>
      </div>

      <template v-else>
        <div class="result-header" v-if="hasSearched">
          <div class="result-summary">
            <span class="result-period">{{ dateRange[0].format('YYYY.MM.DD') }} ~ {{ dateRange[1].format('YYYY.MM.DD') }}</span>
            <span class="result-total">총 <strong>{{ serverTotal.toLocaleString('ko-KR') }}</strong>건</span>
            <a-select v-model:value="sortOrder" size="small" class="sort-select" @change="onSortChange">
              <a-select-option value="desc">최신순</a-select-option>
              <a-select-option value="asc">오래된순</a-select-option>
            </a-select>
          </div>
        </div>

        <!-- 뷰 모드: 주문 / 반품 / 교환 -->
        <a-radio-group v-model:value="viewMode" button-style="solid" size="small" class="view-mode" @change="handleViewModeChange">
          <a-radio-button value="orders">주문</a-radio-button>
          <a-radio-button value="return">반품 <a-badge :count="tabCounts.return" :show-zero="true" :overflow-count="9999" class="tab-badge" /></a-radio-button>
          <a-radio-button value="exchange">교환 <a-badge :count="tabCounts.exchange" :show-zero="true" :overflow-count="9999" class="tab-badge" /></a-radio-button>
        </a-radio-group>

        <!-- 주문 상태 탭 (주문 뷰에서만) -->
        <a-tabs v-if="viewMode === 'orders'" v-model:activeKey="statusTab" @change="handleStatusTabChange" class="result-tabs">
          <a-tab-pane key="all">
            <template #tab>전체 <a-badge :count="tabCounts.all" :show-zero="true" :overflow-count="9999" class="tab-badge" /></template>
          </a-tab-pane>
          <a-tab-pane key="결제완료">
            <template #tab>결제완료 <a-badge :count="tabCounts.paid" :show-zero="true" :overflow-count="9999" class="tab-badge" /></template>
          </a-tab-pane>
          <a-tab-pane key="배송준비">
            <template #tab>배송준비 <a-badge :count="tabCounts.preparing" :show-zero="true" :overflow-count="9999" class="tab-badge" /></template>
          </a-tab-pane>
          <a-tab-pane key="배송중">
            <template #tab>배송중 <a-badge :count="tabCounts.shipping" :show-zero="true" :overflow-count="9999" class="tab-badge" /></template>
          </a-tab-pane>
          <a-tab-pane key="배송완료">
            <template #tab>배송완료 <a-badge :count="tabCounts.delivered" :show-zero="true" :overflow-count="9999" class="tab-badge" /></template>
          </a-tab-pane>
        </a-tabs>

        <div v-if="isLoading" class="loading-wrap"><a-spin size="large" tip="주문 조회 중..." /></div>

        <div v-else-if="orders.length === 0 && hasSearched" class="empty-state">
          <a-empty>
            <template #description>
              <span class="empty-title">조회된 {{ viewModeLabel }}이 없습니다</span>
              <p class="empty-desc">선택한 기간 및 조건에 해당하는 {{ viewModeLabel }}이 없습니다.</p>
            </template>
          </a-empty>
        </div>

        <div v-else-if="orders.length > 0" class="order-list">
          <div v-for="order in orders" :key="order.ord_prd_no || order.ord_no + (order.prd_code || '')" class="order-card" @click="openDetail(order)">
            <div class="card-top">
              <div style="display: flex; align-items: center; gap: 4px" @click.stop>
                <span class="ord-no">{{ order.ord_no }}</span>
                <a-typography-paragraph
                  :copyable="{ text: order.ord_no, tooltip: false }"
                  :style="{ marginBottom: 0, display: 'inline-flex' }"
                />
              </div>
              <div class="card-tags">
                <a-tag :color="statusColor(order.item_status)">{{ statusLabel(order.item_status) }}</a-tag>
                <a-tag v-if="order.partner_code" color="default">{{ partnerLabel(order.partner_code) }}</a-tag>
                <a-tag v-if="order.paymethod" color="default">{{ order.paymethod }}</a-tag>
              </div>
            </div>
            <div class="card-mid">
              <div class="prd-name">{{ order.prd_name || '-' }}</div>
              <div class="prd-qty">{{ order.item_cnt }}개 &times; {{ formatPrice(order.item_price) }}원</div>
              <div v-if="order.return_tracking_number" class="return-tracking-row">
                <span class="return-tracking-label">반품 운송장</span>
                <span class="return-tracking-no">{{ order.return_tracking_number }}</span>
              </div>
            </div>
            <div class="card-bottom">
              <div class="card-bottom-left">
                <span class="ord-company">{{ order.ord_company || order.ord_name || '-' }}</span>
                <span class="ord-date">{{ formatDate(order.ord_date) }}</span>
              </div>
              <div class="ord-price">{{ formatPrice(order.ord_price) }}원</div>
            </div>
            <div v-if="(viewMode === 'orders' && (order.item_status === 'shipped' || order.item_status === 'delivered')) || (viewMode === 'return' && order.item_status === 'return_requested')" class="card-action" @click.stop>
              <a-button v-if="viewMode === 'orders'" size="small" @click.stop="openReturnRequestFromList(order)">반품 접수</a-button>
              <a-button v-if="viewMode === 'return' && !order.return_tracking_number" size="small" type="default" @click.stop="openShipmentRegister(order)">운송장 등록</a-button>
              <a-button v-if="viewMode === 'return'" size="small" type="primary" @click.stop="confirmReturnReceived(order)">수령 확인</a-button>
            </div>
          </div>
        </div>

        <div v-if="serverTotal > 0" class="pagination-wrap">
          <a-pagination v-model:current="currentPage" :total="serverTotal" :page-size="pageSize" :show-size-changer="true" :page-size-options="['20', '50', '100']" :show-total="(total) => `총 ${total.toLocaleString('ko-KR')}건`" @change="handlePageChange" @showSizeChange="handlePageSizeChange" />
        </div>
      </template>
    </div>

    <!-- 상세 모달 -->
    <a-modal v-model:open="detailVisible" :title="`주문 상세 - ${selectedOrder ? selectedOrder.ord_no : ''}`" :footer="null" :width="isMobile ? '100%' : 720" :style="isMobile ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : {}" :body-style="isMobile ? { overflowY: 'auto', maxHeight: '90vh' } : {}">
      <div v-if="selectedOrder" class="detail-modal-body">
        <a-descriptions title="주문 정보" bordered :column="isMobile ? 1 : 2" size="small" class="detail-section">
          <a-descriptions-item label="주문번호">{{ selectedOrder.ord_no }}</a-descriptions-item>
          <a-descriptions-item label="상태"><a-tag :color="statusColor(selectedOrder.item_status)">{{ statusLabel(selectedOrder.item_status) }}</a-tag></a-descriptions-item>
          <a-descriptions-item label="주문일자">{{ formatDate(selectedOrder.ord_date) }}</a-descriptions-item>
          <a-descriptions-item label="결제일자">{{ formatDate(selectedOrder.pay_date) }}</a-descriptions-item>
          <a-descriptions-item label="결제방식">{{ selectedOrder.paymethod || '-' }}</a-descriptions-item>
          <a-descriptions-item label="입점사">{{ partnerLabel(selectedOrder.partner_code) }}</a-descriptions-item>
          <a-descriptions-item label="주문금액" :span="2">{{ formatPrice(selectedOrder.ord_price) }}원</a-descriptions-item>
        </a-descriptions>
        <a-descriptions title="상품 정보" bordered :column="isMobile ? 1 : 2" size="small" class="detail-section">
          <a-descriptions-item label="상품명" :span="2">{{ selectedOrder.prd_name || '-' }}</a-descriptions-item>
          <a-descriptions-item label="품목명" :span="2" v-if="selectedOrder.item_name">{{ selectedOrder.item_name }}</a-descriptions-item>
          <a-descriptions-item label="수량">{{ selectedOrder.item_cnt }}개</a-descriptions-item>
          <a-descriptions-item label="단가">{{ formatPrice(selectedOrder.item_price) }}원</a-descriptions-item>
          <a-descriptions-item label="상품코드">{{ selectedOrder.prd_code || '-' }}</a-descriptions-item>
          <a-descriptions-item label="거래처코드">{{ selectedOrder.cust_no || '-' }}</a-descriptions-item>
        </a-descriptions>
        <a-descriptions title="주문자 정보" bordered :column="isMobile ? 1 : 2" size="small" class="detail-section">
          <a-descriptions-item label="주문자명">{{ selectedOrder.ord_name || '-' }}</a-descriptions-item>
          <a-descriptions-item label="업체명">{{ selectedOrder.ord_company || '-' }}</a-descriptions-item>
          <a-descriptions-item label="연락처">{{ selectedOrder.order_hp || selectedOrder.order_tel || '-' }}</a-descriptions-item>
          <a-descriptions-item label="사업자번호">{{ selectedOrder.ord_company_num || '-' }}</a-descriptions-item>
        </a-descriptions>
        <a-descriptions title="배송 정보" bordered :column="isMobile ? 1 : 2" size="small" class="detail-section">
          <a-descriptions-item label="수령인">{{ selectedOrder.recv_name || '-' }}</a-descriptions-item>
          <a-descriptions-item label="연락처">{{ selectedOrder.recv_hp || selectedOrder.recv_tel || '-' }}</a-descriptions-item>
          <a-descriptions-item label="주소" :span="2">{{ [selectedOrder.recv_addr1, selectedOrder.recv_addr2].filter(Boolean).join(' ') || '-' }} ({{ (selectedOrder.recv_zipcode || '').trim() }})</a-descriptions-item>
          <a-descriptions-item label="희망배송일" v-if="selectedOrder.hope_deli_sdate">{{ formatDate(selectedOrder.hope_deli_sdate) }}</a-descriptions-item>
          <a-descriptions-item label="배송비" :span="selectedOrder.hope_deli_sdate ? 1 : 2">{{ formatPrice(selectedOrder.ship_fee) }}원</a-descriptions-item>
        </a-descriptions>
        <a-descriptions v-if="selectedOrder.ord_memo" title="주문 메모" bordered :column="1" size="small" class="detail-section">
          <a-descriptions-item label="메모">{{ selectedOrder.ord_memo }}</a-descriptions-item>
        </a-descriptions>
        <a-descriptions v-if="hasReturnInfo(selectedOrder)" title="반품 정보" bordered :column="isMobile ? 1 : 2" size="small" class="detail-section">
          <a-descriptions-item label="접수일시">{{ formatDate(selectedOrder.return_requested_at || selectedOrder.return_date) }}</a-descriptions-item>
          <a-descriptions-item label="반품사유">{{ returnReasonLabel(selectedOrder) }}</a-descriptions-item>
          <a-descriptions-item label="수거방법">{{ pickupMethodLabel(selectedOrder.return_pickup_method) }}</a-descriptions-item>
          <a-descriptions-item label="반품 운송장">{{ selectedOrder.return_tracking_number || '-' }}</a-descriptions-item>
          <a-descriptions-item label="메모" :span="isMobile ? 1 : 2">{{ selectedOrder.return_memo || '-' }}</a-descriptions-item>
        </a-descriptions>
        <a-descriptions v-if="viewMode === 'exchange' && (selectedOrder.exchange_date || selectedOrder.exchange_reason)" title="교환 정보" bordered :column="isMobile ? 1 : 2" size="small" class="detail-section">
          <a-descriptions-item label="교환일">{{ formatDate(selectedOrder.exchange_date) }}</a-descriptions-item>
          <a-descriptions-item label="교환 주문품목번호">{{ selectedOrder.new_ord_prd_no || '-' }}</a-descriptions-item>
          <a-descriptions-item label="교환사유" :span="isMobile ? 1 : 2">{{ selectedOrder.exchange_reason || '-' }}</a-descriptions-item>
        </a-descriptions>
        <div v-if="selectedOrder && (selectedOrder.item_status === 'delivered' || selectedOrder.item_status === 'shipped')" class="detail-actions">
          <a-button type="primary" danger @click="openReturnRequest">반품 접수</a-button>
        </div>
      </div>
    </a-modal>

    <return-request-modal
      v-model:visible="returnRequestVisible"
      :order="returnRequestOrder"
      @success="onReturnRequestSuccess"
    />
  </div>
</template>

<script>
import { getProfile } from '@/utils/tokenStore';
import dayjs from 'dayjs';
import PageHeader from '@/views/components/PageHeader.vue';
import ReturnRequestModal from './ReturnRequestModal.vue';
import brightmallOrdersApi from '@/apis/brightmallOrders.js';
import { Modal } from 'ant-design-vue';
import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons-vue';

const PAGE_SIZE = 20;
const MAX_DAYS = 180;
const DEBOUNCE_MS = 500;

export default {
  name: 'BrightmallOrders',
  components: { PageHeader, ReturnRequestModal, SearchOutlined, ExclamationCircleOutlined },
  data() {
    const q = this.$route?.query || {};
    return {
      dateRange: [dayjs().subtract(30, 'day'), dayjs()],
      viewMode: q.view || 'orders',
      statusTab: q.status || 'all',
      filters: { ord_no: q.ord_no || '', company_name: q.company || '', prd_name: q.prd || '', paymethod: q.pay || '', partner_code: q.partner || '' },
      sortOrder: q.sort || 'desc',
      orders: [],
      serverTotal: 0,
      tabCounts: { all: 0, paid: 0, preparing: 0, shipping: 0, delivered: 0, return: 0, exchange: 0 },
      isLoading: false,
      // 연타/중복 호출 코얼레싱: 진행 중이면 큐잉 후 완료 시 1회만 재실행 (vendor-api 레이트리밋 방어)
      ordersRefetchQueued: false,
      isCountLoading: false,
      countsRefetchQueued: false,
      noCustSeqs: false,
      hasSearched: false,
      currentPage: parseInt(q.page) || 1,
      pageSize: PAGE_SIZE,
      detailVisible: false,
      selectedOrder: null,
      returnRequestVisible: false,
      returnRequestOrder: null,
      isMobile: window.innerWidth <= 767,
      filterExpanded: window.innerWidth > 767,
      debounceTimer: null,
      paymethods: [
        { value: '카드결제', label: '카드결제' },
        { value: '선수금', label: '선수금' },
        { value: '무통장입금', label: '무통장입금' },
        { value: '가상계좌', label: '가상계좌' },
        { value: '기타', label: '기타' },
      ],
      partnerCodes: [
        { value: 'DNTM', label: '덴티움(자사)' },
        { value: 'SEIL', label: '세일글로벌' },
      ],
      datePresets: [
        { label: '오늘', days: 0 },
        { label: '7일', days: 7 },
        { label: '30일', days: 30 },
        { label: '90일', days: 90 },
        { label: '180일', days: 180 },
      ],
    };
  },
  computed: {
    dateRangeWarning() {
      if (!this.dateRange || this.dateRange.length < 2) return '';
      const days = this.dateRange[1].diff(this.dateRange[0], 'day');
      if (days < 0) return '시작일이 종료일보다 클 수 없습니다.';
      if (days > MAX_DAYS) return `조회 기간은 최대 ${MAX_DAYS}일까지 가능합니다. (현재 ${days}일)`;
      return '';
    },
    viewModeLabel() {
      return { orders: '주문', return: '반품', exchange: '교환' }[this.viewMode] || '주문';
    },
  },
  async mounted() {
    window.addEventListener('resize', this.onResize);
    await this.fetchOrders();
    this.fetchTabCounts();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    onResize() { this.isMobile = window.innerWidth <= 767; this.filterExpanded = !this.isMobile; },
    toggleFilter() { this.filterExpanded = !this.filterExpanded; },
    disabledDate(current) { return current && current > dayjs().endOf('day'); },
    isActivePreset(preset) {
      if (!this.dateRange || this.dateRange.length < 2) return false;
      const exp = [dayjs().subtract(preset.days, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')];
      return this.dateRange[0].format('YYYY-MM-DD') === exp[0] && this.dateRange[1].format('YYYY-MM-DD') === exp[1];
    },
    syncQuery() {
      const q = {};
      if (this.viewMode !== 'orders') q.view = this.viewMode;
      if (this.statusTab !== 'all') q.status = this.statusTab;
      if (this.currentPage > 1) q.page = this.currentPage;
      if (this.filters.ord_no) q.ord_no = this.filters.ord_no;
      if (this.filters.company_name) q.company = this.filters.company_name;
      if (this.filters.prd_name) q.prd = this.filters.prd_name;
      if (this.filters.paymethod) q.pay = this.filters.paymethod;
      if (this.filters.partner_code) q.partner = this.filters.partner_code;
      if (this.sortOrder !== 'desc') q.sort = this.sortOrder;
      this.$router?.replace({ query: q }).catch(() => {});
    },
    applyPreset(preset) {
      this.dateRange = [dayjs().subtract(preset.days, 'day'), dayjs()];
      this.resetPage(); this.fetchOrders(); this.fetchTabCounts();
    },
    onDateChange() {
      this.resetPage();
      this.$nextTick(() => { this.fetchOrders(); this.fetchTabCounts(); });
    },
    onFilterInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => { this.resetPage(); this.fetchOrders(); this.fetchTabCounts(); }, DEBOUNCE_MS);
    },
    onFilterImmediate() { this.resetPage(); this.fetchOrders(); this.fetchTabCounts(); },
    handleViewModeChange() { this.resetPage(); this.fetchOrders(); this.fetchTabCounts(); },
    handleStatusTabChange() { this.resetPage(); this.fetchOrders(); },
    handlePageChange(page) { this.currentPage = page; this.fetchOrders(); window.scrollTo({ top: 0, behavior: 'smooth' }); },
    handlePageSizeChange(current, size) { this.pageSize = size; this.resetPage(); this.fetchOrders(); },
    resetPage() { this.currentPage = 1; },
    buildOrderParams() {
      const params = { start_date: this.dateRange[0].format('YYYY-MM-DD'), end_date: this.dateRange[1].format('YYYY-MM-DD'), page: this.currentPage, limit: this.pageSize };
      if (this.statusTab !== 'all') params.status = this.statusTab;
      if (this.filters.ord_no) params.ord_no = this.filters.ord_no;
      if (this.filters.company_name) params.company_name = this.filters.company_name;
      if (this.filters.prd_name) params.prd_name = this.filters.prd_name;
      if (this.filters.paymethod) params.paymethod = this.filters.paymethod;
      if (this.filters.partner_code) params.partner_code = this.filters.partner_code;
      if (this.sortOrder) params.sort = this.sortOrder;
      return params;
    },
    buildDateParams(overrides = {}) {
      return { start_date: this.dateRange[0].format('YYYY-MM-DD'), end_date: this.dateRange[1].format('YYYY-MM-DD'), limit: 0, ...overrides };
    },
    async fetchOrders() {
      if (this.dateRangeWarning) return;
      // 진행 중이면 중복 발사하지 않고 완료 후 1회만 재실행 (최신 필터/페이지 상태로 실행됨)
      if (this.isLoading) { this.ordersRefetchQueued = true; return; }
      this.isLoading = true;
      this.syncQuery();
      try {
        let res;
        if (this.viewMode === 'return') {
          // 반품/교환 엔드포인트는 paymethod, partner_code 필터를 지원하지 않으므로 제외
          const returnParams = this.buildDateParams({ page: this.currentPage, limit: this.pageSize });
          if (this.filters.ord_no) returnParams.ord_no = this.filters.ord_no;
          if (this.filters.company_name) returnParams.company_name = this.filters.company_name;
          if (this.filters.prd_name) returnParams.prd_name = this.filters.prd_name;
          res = await brightmallOrdersApi.getReturns(returnParams);
          this.orders = res.data?.return_list || [];
          this.serverTotal = res.data?.pagination?.total || 0;
        } else if (this.viewMode === 'exchange') {
          // 반품/교환 엔드포인트는 paymethod, partner_code 필터를 지원하지 않으므로 제외
          const exchangeParams = this.buildDateParams({ page: this.currentPage, limit: this.pageSize });
          if (this.filters.ord_no) exchangeParams.ord_no = this.filters.ord_no;
          if (this.filters.company_name) exchangeParams.company_name = this.filters.company_name;
          if (this.filters.prd_name) exchangeParams.prd_name = this.filters.prd_name;
          res = await brightmallOrdersApi.getExchanges(exchangeParams);
          this.orders = res.data?.exchange_list || [];
          this.serverTotal = res.data?.pagination?.total || 0;
        } else {
          res = await brightmallOrdersApi.getOrders(this.buildOrderParams());
          this.orders = res.data?.order_list || [];
          this.serverTotal = res.data?.pagination?.total || 0;
        }
        if (this.orders.length === 0 && this.serverTotal === 0 && !this.hasSearched) {
          try {
            const info = JSON.parse(getProfile('CompanyInfo') || '{}');
            const seqs = info?.accountInfo?.custSeqs;
            if (!Array.isArray(seqs) || seqs.length === 0) this.noCustSeqs = true;
          } catch { /* ignore */ }
        }
        this.hasSearched = true;
      } catch { this.$message?.error('주문 조회 중 오류가 발생했습니다.'); } finally {
        this.isLoading = false;
        if (this.ordersRefetchQueued) { this.ordersRefetchQueued = false; this.fetchOrders(); }
      }
    },
    async fetchTabCounts() {
      if (this.dateRangeWarning) return;
      // 탭 카운트는 요청 7건 묶음이라 중복 발사 시 레이트리밋을 빠르게 소진 — 반드시 코얼레싱
      if (this.isCountLoading) { this.countsRefetchQueued = true; return; }
      this.isCountLoading = true;
      const base = this.buildDateParams();
      if (this.filters.paymethod) base.paymethod = this.filters.paymethod;
      if (this.filters.partner_code) base.partner_code = this.filters.partner_code;
      if (this.filters.ord_no) base.ord_no = this.filters.ord_no;
      if (this.filters.company_name) base.company_name = this.filters.company_name;
      if (this.filters.prd_name) base.prd_name = this.filters.prd_name;
      // 반품/교환 탭 카운트: paymethod, partner_code는 백엔드 미지원 파라미터이므로 제외
      const returnExchangeBase = this.buildDateParams();
      if (this.filters.ord_no) returnExchangeBase.ord_no = this.filters.ord_no;
      if (this.filters.company_name) returnExchangeBase.company_name = this.filters.company_name;
      if (this.filters.prd_name) returnExchangeBase.prd_name = this.filters.prd_name;
      try {
        const [allRes, paidRes, prepRes, shipRes, delRes, retRes, exRes] = await Promise.all([
          brightmallOrdersApi.getOrders(base).catch(() => ({ data: {} })),
          brightmallOrdersApi.getOrders({ ...base, status: '결제완료' }).catch(() => ({ data: {} })),
          brightmallOrdersApi.getOrders({ ...base, status: '배송준비' }).catch(() => ({ data: {} })),
          brightmallOrdersApi.getOrders({ ...base, status: '배송중' }).catch(() => ({ data: {} })),
          brightmallOrdersApi.getOrders({ ...base, status: '배송완료' }).catch(() => ({ data: {} })),
          brightmallOrdersApi.getReturns(returnExchangeBase).catch(() => ({ data: {} })),
          brightmallOrdersApi.getExchanges(returnExchangeBase).catch(() => ({ data: {} })),
        ]);
        this.tabCounts = {
          all: allRes.data?.pagination?.total || 0, paid: paidRes.data?.pagination?.total || 0,
          preparing: prepRes.data?.pagination?.total || 0, shipping: shipRes.data?.pagination?.total || 0,
          delivered: delRes.data?.pagination?.total || 0, return: retRes.data?.pagination?.total || 0,
          exchange: exRes.data?.pagination?.total || 0,
        };
      } catch { /* ignore */ } finally {
        this.isCountLoading = false;
        if (this.countsRefetchQueued) { this.countsRefetchQueued = false; this.fetchTabCounts(); }
      }
    },
    resetFilters() {
      this.filters = { ord_no: '', company_name: '', prd_name: '', paymethod: '', partner_code: '' };
      this.dateRange = [dayjs().subtract(30, 'day'), dayjs()];
      this.statusTab = 'all';
      this.sortOrder = 'desc';
      this.resetPage();
      this.fetchOrders();
      this.fetchTabCounts();
    },
    onSortChange() { this.resetPage(); this.fetchOrders(); },
    partnerLabel(code) {
      if (!code) return '-';
      const map = { DNTM: '덴티움(자사)', SEIL: '세일글로벌' };
      return map[code] || code;
    },
    hasReturnInfo(order) {
      if (!order) return false;
      return !!(order.return_requested_at || order.return_date || order.return_reason || order.return_reason_code || order.return_tracking_number);
    },
    returnReasonLabel(order) {
      const codeMap = { defective: '불량/하자', wrong_item: '오배송', changed_mind: '단순변심', damaged_shipping: '배송 중 파손', other: '기타' };
      const codeLabel = codeMap[order.return_reason_code];
      const detail = order.return_reason;
      if (codeLabel && detail) return `${codeLabel} - ${detail}`;
      return codeLabel || detail || '-';
    },
    pickupMethodLabel(method) {
      const map = { courier_pickup: '택배수거', direct_return: '직접회수', store_visit: '매장방문' };
      return map[method] || method || '-';
    },
    openDetail(order) { this.selectedOrder = order; this.detailVisible = true; },
    openReturnRequest() {
      this.returnRequestOrder = this.selectedOrder;
      this.detailVisible = false;
      this.$nextTick(() => { this.returnRequestVisible = true; });
    },
    openReturnRequestFromList(order) {
      this.returnRequestOrder = order;
      this.returnRequestVisible = true;
    },
    async openShipmentRegister(order) {
      const { Modal, Input, Select } = await import('ant-design-vue');
      const carrierRef = { value: 'cj' };
      const trackingRef = { value: '' };
      const h = (await import('vue')).h;
      Modal.confirm({
        title: '반품 운송장 등록',
        width: 480,
        content: h('div', { style: 'margin-top: 16px' }, [
          h('div', { style: 'margin-bottom: 12px' }, [
            h('div', { style: 'font-size: 13px; color: #8d939a; margin-bottom: 4px' }, '택배사'),
            h(Select, {
              value: carrierRef.value,
              style: 'width: 100%',
              onChange: (v) => { carrierRef.value = v; },
              options: [
                { value: 'cj', label: 'CJ대한통운' },
                { value: 'hanjin', label: '한진택배' },
                { value: 'lotte', label: '롯데택배' },
                { value: 'logen', label: '로젠택배' },
                { value: 'epost', label: '우체국택배' },
                { value: 'kdexp', label: '경동택배' },
              ],
            }),
          ]),
          h('div', {}, [
            h('div', { style: 'font-size: 13px; color: #8d939a; margin-bottom: 4px' }, '운송장 번호'),
            h(Input, {
              placeholder: '운송장 번호 입력',
              onChange: (e) => { trackingRef.value = e.target.value; },
            }),
          ]),
        ]),
        okText: '등록',
        cancelText: '취소',
        onOk: async () => {
          if (!trackingRef.value.trim()) {
            this.$message?.warning('운송장 번호를 입력해주세요');
            return Promise.reject();
          }
          try {
            await brightmallOrdersApi.registerReturnShipment({
              shipments: [{
                ord_prd_no: order.ord_prd_no,
                return_carrier_code: carrierRef.value,
                return_tracking_number: trackingRef.value.trim(),
              }],
            });
            this.$message?.success('운송장이 등록되었습니다');
            this.fetchOrders();
            this.fetchTabCounts();
          } catch (e) {
            this.$message?.error('운송장 등록에 실패했습니다');
            return Promise.reject();
          }
        },
      });
    },
    onReturnRequestSuccess() {
      this.returnRequestVisible = false;
      this.fetchOrders();
      this.fetchTabCounts();
    },
    async confirmReturnReceived(order) {
      Modal.confirm({
        title: '수령 확인',
        content: `${order.prd_name} - 수령을 확인하시겠습니까?`,
        okText: '수령 확인',
        cancelText: '취소',
        onOk: async () => {
          try {
            await brightmallOrdersApi.confirmReturn({
              returns: [{ ord_prd_no: order.ord_prd_no, condition: 'good' }],
            });
            this.$message?.success('수령이 확인되었습니다.');
            this.fetchOrders();
            this.fetchTabCounts();
          } catch {
            this.$message?.error('수령 확인에 실패했습니다.');
          }
        },
      });
    },
    formatDate(val) { if (!val) return '-'; return dayjs(val).isValid() ? dayjs(val).format('YYYY-MM-DD') : val; },
    formatPrice(val) { if (val === null || val === undefined || val === '') return '0'; return Number(val).toLocaleString('ko-KR'); },
    statusLabel(status) {
      if (!status) return '-';
      const map = { pending: '결제완료', preparing: '배송준비', shipped: '배송중', delivered: '배송완료', cancelled: '취소됨', cancel_requested: '취소요청', return_requested: '반품요청', exchange_requested: '교환요청', returned: '반품완료', exchanged: '교환완료' };
      return map[status] || status;
    },
    statusColor(status) {
      if (!status) return 'default';
      const map = { pending: 'blue', preparing: 'cyan', shipped: 'geekblue', delivered: 'green', cancelled: 'red', cancel_requested: 'orange', return_requested: 'orange', exchange_requested: 'orange', returned: 'purple', exchanged: 'purple' };
      return map[status] || 'default';
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-section { background: #fff; border: 1px solid #e8ebed; border-radius: 8px; margin-bottom: 16px; overflow: hidden; }
.filter-section-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: #fafbfc; border-bottom: 1px solid #e8ebed;
  .filter-title { font-size: 14px; font-weight: 600; color: #2f3337; } .filter-hint { font-size: 12px; color: #acb2b9; } }
.filter-body { padding: 16px 20px; }
.filter-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; &:last-child { margin-bottom: 0; }
  .filter-label { font-size: 13px; font-weight: 500; color: #4b5158; flex-shrink: 0; width: 60px; } }
.filter-input { width: 150px; flex-shrink: 0; }
.filter-select { width: 120px; flex-shrink: 0; }
.quick-dates { display: flex; gap: 4px; flex-shrink: 0; }
.date-picker { width: 260px; flex-shrink: 0; }
.date-warning { display: flex; align-items: center; gap: 4px; font-size: 13px; color: #fa8c16; margin-top: 8px; }
.result-section { background: #fff; border: 1px solid #e8ebed; border-radius: 8px; padding: 20px; min-height: 300px; }
.result-header { margin-bottom: 4px; }
.result-summary { display: flex; align-items: center; gap: 12px; padding-bottom: 12px;
  .result-period { font-size: 13px; color: #8d939a; padding: 4px 10px; background: #f5f6f7; border-radius: 4px; }
  .result-total { font-size: 13px; color: #6e747c; strong { color: #1677ff; font-size: 15px; } }
  .sort-select { width: 100px; margin-left: auto; } }
.view-mode { margin-bottom: 12px; }
.result-tabs { :deep(.ant-tabs-nav) { margin-bottom: 16px; } }
.tab-badge { margin-left: 6px; :deep(.ant-badge-count) { background-color: #d9d9d9; color: #595959; box-shadow: none; font-size: 11px; } }
.empty-state { padding: 60px 0; text-align: center;
  .empty-title { font-size: 15px; font-weight: 600; color: #4b5158; } .empty-desc { font-size: 13px; color: #acb2b9; margin-top: 8px; line-height: 1.6; } }
.loading-wrap { display: flex; justify-content: center; align-items: center; padding: 80px 0; }
.order-list { display: flex; flex-direction: column; gap: 10px; }
.order-card { background: #fff; border: 1px solid #e8ebed; border-radius: 8px; padding: 16px 20px; cursor: pointer; transition: box-shadow 0.2s, border-color 0.2s;
  &:hover { border-color: #1677ff; box-shadow: 0 2px 8px rgba(22, 119, 255, 0.1); } }
.card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;
  .ord-no { font-size: 13px; color: #8d939a; font-weight: 500; font-family: 'SF Mono', 'Menlo', monospace; }
  .card-tags { display: flex; gap: 4px; flex-shrink: 0; } }
.card-mid { margin-bottom: 12px; .prd-name { font-size: 15px; font-weight: 700; color: #2f3337; margin-bottom: 4px; word-break: keep-all; } .prd-qty { font-size: 13px; color: #6e747c; } .return-tracking-row { margin-top: 6px; font-size: 13px; .return-tracking-label { display: inline-block; padding: 1px 6px; margin-right: 6px; border-radius: 4px; background: #fff7e6; color: #d46b08; font-size: 12px; } .return-tracking-no { color: #2f3337; font-weight: 600; letter-spacing: 0.3px; } } }
.card-bottom { display: flex; justify-content: space-between; align-items: flex-end;
  .card-bottom-left { display: flex; flex-direction: column; gap: 2px; .ord-company { font-size: 13px; font-weight: 600; color: #4b5158; } .ord-date { font-size: 12px; color: #acb2b9; } }
  .ord-price { font-size: 17px; font-weight: 700; color: #2f3337; white-space: nowrap; } }
.pagination-wrap { display: flex; justify-content: center; margin-top: 24px; }
.detail-modal-body { padding: 8px 24px; }
.detail-section { margin-bottom: 24px; &:last-child { margin-bottom: 0; } }
.detail-actions { position: sticky; bottom: 0; background: #fff; padding: 16px 0; border-top: 1px solid #f0f0f0; display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.card-action { margin-top: 8px; display: flex; justify-content: flex-end; }
// 태블릿 (768px ~ 1024px)
@media screen and (max-width: 1024px) {
  .filter-row { flex-wrap: wrap; gap: 8px;
    .filter-input, .filter-select { flex: 1; min-width: 160px; }
    .ant-btn { min-height: 40px; }
  }
  .quick-dates .ant-btn { min-height: 40px; padding: 0 14px; }
  .order-card { padding: 14px 16px; }
  :deep(.ant-pagination) {
    .ant-pagination-item, .ant-pagination-prev, .ant-pagination-next { min-width: 40px; min-height: 40px; line-height: 40px; }
  }
  :deep(.ant-modal) { max-width: 90vw;
    .ant-modal-body { max-height: 80dvh; overflow-y: auto; -webkit-overflow-scrolling: touch; }
  }
}

// 모바일 (~ 767px)
@media screen and (max-width: 767px) {
  // 필터 Progressive Disclosure
  .filter-section {
    .filter-section-header { cursor: pointer; -webkit-tap-highlight-color: transparent;
      .filter-toggle-icon { font-size: 12px; color: #8d939a; transition: transform 0.2s ease; }
    }
    .filter-body { overflow: hidden; max-height: 0; opacity: 0; padding: 0 20px; transition: max-height 0.3s ease, opacity 0.2s ease, padding 0.3s ease; }
    &.filter-expanded .filter-body { max-height: 800px; opacity: 1; padding: 16px 20px; }
  }

  // 필터 행 - 세로 배치
  .filter-row { flex-direction: column; align-items: stretch; gap: 10px; margin-bottom: 12px;
    .filter-label { width: auto; margin-bottom: 2px; font-size: 13px; }
  }
  .filter-input, .filter-select { width: 100% !important; }
  :deep(.ant-select-selector) { min-height: 44px !important; }
  :deep(.ant-input) { min-height: 44px; font-size: 16px; } // iOS 줌 방지

  // 빠른 날짜 - 줄바꿈으로 모든 버튼 표시
  .quick-dates { display: flex; flex-wrap: wrap; gap: 8px; padding: 4px 0 8px;
    .ant-btn { flex-shrink: 0; min-height: 40px; min-width: auto; padding: 0 14px; border-radius: 22px; white-space: nowrap; }
  }
  .date-picker { width: 100% !important; }

  // 검색/초기화 버튼
  .filter-row .ant-btn { min-height: 48px; font-size: 15px; width: 100%; }

  // 결과 요약
  .result-summary { flex-direction: column; align-items: flex-start; gap: 6px; padding-bottom: 12px;
    .sort-select { margin-left: 0; width: 120px; }
  }

  // 뷰 모드 라디오 (주문/반품/교환) - 세그먼트 스타일
  .view-mode { width: 100%; margin-bottom: 12px;
    :deep(.ant-radio-group) { display: flex; width: 100%; background: #f5f5f5; border-radius: 10px; padding: 4px;
      .ant-radio-button-wrapper { flex: 1; text-align: center; min-height: 44px; display: flex; align-items: center; justify-content: center;
        border: none; border-radius: 8px; background: transparent; font-size: 14px;
        &::before { display: none; }
        &.ant-radio-button-wrapper-checked { background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); font-weight: 600; }
      }
    }
  }

  // 탭 바 - 수평 스크롤
  .result-tabs :deep(.ant-tabs) {
    .ant-tabs-nav-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch;
      scrollbar-width: none; -ms-overflow-style: none; &::-webkit-scrollbar { display: none; }
    }
    .ant-tabs-nav-list { flex-wrap: nowrap; }
    .ant-tabs-tab { flex-shrink: 0; padding: 12px 14px; min-height: 44px; white-space: nowrap; }
    .ant-tabs-nav-operations { display: none !important; }
  }

  // 주문 카드 - 터치 최적화
  .order-card { padding: 16px; border-radius: 12px; -webkit-tap-highlight-color: transparent;
    &:active { background-color: #f9f9f9; }
    &:hover { border-color: #e8ebed; box-shadow: none; } // 모바일 hover 비활성화
  }
  .card-top { flex-wrap: wrap; gap: 8px; margin-bottom: 12px;
    .ord-no { font-size: 12px; }
    .card-tags { gap: 4px; flex-wrap: wrap; :deep(.ant-tag) { font-size: 11px; padding: 2px 6px; margin: 0; } }
  }
  .card-mid { margin-bottom: 12px;
    .prd-name { font-size: 14px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .prd-qty { font-size: 13px; }
  }
  .card-bottom { min-height: 44px;
    .card-bottom-left { .ord-company { font-size: 13px; } .ord-date { font-size: 12px; } }
    .ord-price { font-size: 16px; }
  }

  // 페이지네이션
  .pagination-wrap { padding: 16px 0; }
  :deep(.ant-pagination) {
    .ant-pagination-item, .ant-pagination-prev, .ant-pagination-next { min-width: 44px; min-height: 44px; line-height: 44px; border-radius: 10px; }
    .ant-pagination-options { display: none; } // 모바일에서 사이즈 셀렉터 숨김
    .ant-pagination-total-text { width: 100%; text-align: center; margin-bottom: 8px; font-size: 13px; min-height: auto; }
  }

  // 상세 모달 - 풀스크린
  :deep(.ant-modal) { top: 0 !important; margin: 0 !important; padding: 0 !important; max-width: 100vw !important; width: 100vw !important;
    .ant-modal-content { border-radius: 0; min-height: 100dvh; display: flex; flex-direction: column; padding-top: env(safe-area-inset-top, 0px); }
    .ant-modal-header { position: sticky; top: 0; z-index: 10; min-height: 56px; display: flex; align-items: center; background: #fff; border-bottom: 1px solid #f0f0f0;
      .ant-modal-title { font-size: 17px; font-weight: 600; }
    }
    .ant-modal-close { width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
      .ant-modal-close-x { width: 44px; height: 44px; line-height: 44px; }
    }
    .ant-modal-body { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior: contain;
      padding: 16px; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
    }
  }
  .detail-modal-body { padding: 4px 8px; }
  .detail-section { margin-bottom: 20px; }
  .detail-actions { position: sticky; bottom: 0; background: #fff; padding: 12px 16px; border-top: 1px solid #f0f0f0; margin: 0 -16px -16px; }

  // iOS 입력 줌 방지
  input, select, textarea { font-size: 16px !important; }

  // Safe Area
  .page-container-v2 { padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); }
}
</style>
